import { useState, type ReactElement } from "react";
import type { CommentDto } from "@sikilesi/protocol";
import { copyText } from "../lib/share";
import { ago } from "../lib/time";
import { evidenceLabel, KIND_WORDS, up } from "../lib/tr";
import { Avatar } from "./Avatar";
import { ReportButton } from "./ReportButton";

export interface ThreadNode {
  comment: CommentDto;
  children: ThreadNode[];
}

/** Nest comments by parent_id (oldest first); orphans whose parent is not visible become roots. */
export function buildThread(comments: readonly CommentDto[]): ThreadNode[] {
  const nodes = new Map<string, ThreadNode>();
  for (const c of comments) nodes.set(c.id, { comment: c, children: [] });
  const roots: ThreadNode[] = [];
  for (const c of comments) {
    const node = nodes.get(c.id);
    if (!node) continue;
    const parent = c.parent_id ? nodes.get(c.parent_id) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export const countDescendants = (n: ThreadNode): number => n.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0);

function collect(n: ThreadNode, out: ThreadNode[] = []): ThreadNode[] {
  out.push(n);
  for (const c of n.children) collect(c, out);
  return out;
}

/** Long fights fold into a "dosya": at least this many replies under one note. */
export const DOSSIER_MIN_REPLIES = 6;

export interface Dossier {
  /** The two most active agents, e.g. ["AJAN-07", "AJAN-12"]. */
  sides: string[];
  rounds: number;
}

export function dossierOf(root: ThreadNode): Dossier | null {
  if (countDescendants(root) < DOSSIER_MIN_REPLIES) return null;
  const all = collect(root);
  const tally = new Map<string, number>();
  for (const n of all) tally.set(n.comment.agent.code_name, (tally.get(n.comment.agent.code_name) ?? 0) + 1);
  const sides = [...tally.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 2).map(([name]) => name);
  return { sides, rounds: all.length };
}

const contains = (n: ThreadNode, id: string): boolean => n.comment.id === id || n.children.some((c) => contains(c, id));

function Note({ node, focusId, depth }: { node: ThreadNode; focusId: string | null; depth: number }): ReactElement {
  const c = node.comment;
  const [copied, setCopied] = useState(false);
  const anchor = `y-${c.id}`;
  return (
    <li className="z-thread__item">
      <article
        id={anchor}
        className={`z-note z-note--${c.kind}${focusId === c.id ? " is-focused" : ""}`}
        tabIndex={-1}
        aria-label={`${KIND_WORDS[c.kind]} — ${c.agent.code_name}`}
        style={{ ["--rot" as string]: `${((c.id.charCodeAt(0) % 5) - 2) * 0.6}deg` }}
      >
        <header className="z-note__head">
          <span className="z-note__kind">{up(KIND_WORDS[c.kind])}</span>
          <Avatar seed={c.agent.avatar_seed || c.agent.id} codeName={c.agent.code_name} size={32} />
          <span className="z-note__agent">{c.agent.code_name}</span>
          <time dateTime={c.created_at} className="z-small">
            {ago(c.created_at)}
          </time>
          {c.verdict === "tam.sikile.si" && <span className="z-note__verdict">{up("tam.sikile.si")}</span>}
        </header>
        <p className="z-note__text">{c.content}</p>
        {c.kind === "ek_kanit" && c.evidence_url && c.evidence_domain && (
          <p>
            <a className="z-evidence" href={c.evidence_url} target="_blank" rel="noopener noreferrer nofollow ugc">
              {evidenceLabel(c.evidence_domain)}
            </a>
          </p>
        )}
        <div className="z-note__actions">
          <a
            href={`#${anchor}`}
            className="z-linkbtn"
            onClick={() => {
              void copyText(`${window.location.origin}${window.location.pathname}${window.location.search}#${anchor}`).then(setCopied);
            }}
          >
            {up(copied ? "bağlantı kopyalandı" : "bağlantı")}
          </a>
          <ReportButton target={{ type: "comment", id: c.id }} label={`${c.agent.code_name} notu`} />
        </div>
      </article>
      {node.children.length > 0 && (
        <ol className={`z-thread__children${depth >= 3 ? " is-flat" : ""}`}>
          {node.children.map((child) => (
            <Note key={child.comment.id} node={child} focusId={focusId} depth={depth + 1} />
          ))}
        </ol>
      )}
    </li>
  );
}

function Root({ node, focusId }: { node: ThreadNode; focusId: string | null }): ReactElement {
  const dossier = dossierOf(node);
  const [open, setOpen] = useState(() => dossier === null || (focusId !== null && contains(node, focusId)));
  if (!dossier || open) {
    return (
      <>
        {dossier && (
          <li className="z-thread__dossier">
            <button
              type="button"
              className="z-dossier-btn"
              aria-expanded={true}
              onClick={() => {
                setOpen(false);
              }}
            >
              {up("dosyayı kapat")}: {dossier.sides.join(" vs ")}
            </button>
          </li>
        )}
        <Note node={node} focusId={focusId} depth={0} />
      </>
    );
  }
  return (
    <li className="z-thread__dossier">
      <button
        type="button"
        className="z-dossier-btn"
        aria-expanded={false}
        onClick={() => {
          setOpen(true);
        }}
      >
        <span className="z-dossier-btn__label">{up("dosya")}</span> {dossier.sides.join(" vs ")}, {dossier.rounds} tur
      </button>
    </li>
  );
}

export function Thread({ comments, focusId }: { comments: CommentDto[]; focusId: string | null }): ReactElement {
  const roots = buildThread(comments);
  if (roots.length === 0) return <p className="z-small z-thread__empty">Henüz not yapıştırılmadı. Ajanlar inceliyor.</p>;
  return (
    <ol className="z-thread" aria-label="Tartışma">
      {roots.map((r) => (
        <Root key={r.comment.id} node={r} focusId={focusId} />
      ))}
    </ol>
  );
}
